import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import {
    getSendHealthService,
    getSendSettingsService,
    saveSendSettingsService,
    saveSendSwitchService,
    sendDryRunService,
    sendRunService,
} from "../../../../Services/CRMAdminServices/CRMOperator";

/**
 * The sender — Fintrack only.
 *
 * This is the screen that can do real damage, so it is built to make the safe
 * thing the easy thing. Everything is off until somebody turns it on. There are
 * two switches: a master one that stops every client at once, and one per
 * client. A new client starts off, so importing a lead list cannot start
 * anything.
 *
 * Read the outbox before the first send. Dry run claims a real batch, merges
 * it, checks it, shows it, and puts it straight back without sending a thing.
 */

const STATES = ["", "VIC", "NSW", "QLD", "SA", "WA", "TAS", "NT", "ACT"];

const hour = (h: number) => {
    const suffix = h < 12 ? "am" : "pm";
    const display = h % 12 === 0 ? 12 : h % 12;
    return `${display}${suffix}`;
};

const when = (v: any) => {
    if (!v) return "—";
    const iso = String(v).endsWith("Z") ? String(v) : `${v}Z`;
    return new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Melbourne" });
};

const CRMSendControl = () => {
    const [data, setData] = useState<any>(null);
    const [health, setHealth] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const [preview, setPreview] = useState<any>(null);
    const [busy, setBusy] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, h]: any[] = await Promise.all([getSendSettingsService(), getSendHealthService()]);
            setData(s?.data ?? null);
            setHealth(h?.data ?? null);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not load the sender.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const master = data?.switch ?? {};
    const companies = data?.companies ?? [];

    const flipMaster = async () => {
        const turningOn = !master.sendingOn;

        if (turningOn && !window.confirm(
            "This turns sending on for every client that is switched on.\n\n" +
            "Have you read the dry run?")) return;

        setBusy("master");
        try {
            await saveSendSwitchService({
                sendingOn: turningOn,
                reason: turningOn ? "Turned on from the CRM." : "Stopped from the CRM.",
            });
            await load();
            toast.success(turningOn ? "Sending is on." : "Everything is stopped.");
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not change the switch.");
        } finally {
            setBusy(null);
        }
    };

    const saveCompany = async (c: any, changes: any) => {
        try {
            await saveSendSettingsService({
                companyId: c.companyId,
                sendingOn: changes.sendingOn ?? c.sendingOn,
                earliestHour: Number(changes.earliestHour ?? c.earliestHour),
                latestHour: Number(changes.latestHour ?? c.latestHour),
                sendOnSunday: changes.sendOnSunday ?? c.sendOnSunday,
                dailyCap: Number(changes.dailyCap ?? c.dailyCap),
                stateCode: changes.stateCode !== undefined ? changes.stateCode : c.stateCode,
            });
            await load();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not save that.");
        }
    };

    const dryRun = async () => {
        setBusy("dry");
        setPreview(null);
        try {
            const r: any = await sendDryRunService({ top: 50 });
            setPreview(r?.data ?? null);
            const n = r?.data?.claimed ?? 0;
            toast.success(n === 0 ? "Nothing is due right now." : `${n} would go out. Nothing was sent.`);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "The dry run failed.");
        } finally {
            setBusy(null);
        }
    };

    const runNow = async () => {
        if (!window.confirm(
            "This sends for real, to real people.\n\n" +
            "Up to 50 messages from whatever is due right now. Continue?")) return;

        setBusy("run");
        try {
            const r: any = await sendRunService({ top: 50 });
            const res = r?.data ?? {};
            toast.success(res.message || "Run finished.");
            setPreview(null);
            await load();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "The run failed.");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="p-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 className="f-w-900 mb-0" style={{ color: "blue" }}>The sender</h3>
                <div className="d-flex align-items-center gap-2">
                    <Button color="secondary" size="sm" outline disabled={busy !== null} onClick={dryRun}>
                        {busy === "dry" ? "Checking…" : "Dry run"}
                    </Button>
                    <Button color="primary" size="sm" disabled={busy !== null || !master.sendingOn} onClick={runNow}>
                        {busy === "run" ? "Sending…" : "Send what is due"}
                    </Button>
                </div>
            </div>

            {/* The master switch */}
            <div className="p-3 mb-3" style={{ ...panel, borderLeft: `6px solid ${master.sendingOn ? "#1a7f37" : "#b0392e"}` }}>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <div className="f-w-700" style={{ fontSize: 15 }}>
                            {master.sendingOn ? "Sending is ON" : "Everything is stopped"}
                        </div>
                        <div className="text-muted" style={{ fontSize: 13 }}>
                            {master.sendingOn
                                ? "Clients switched on below will send inside their own window."
                                : "Nothing goes out, for anybody, whatever the settings below say."}
                            {master.reason && <> · {master.reason}</>}
                            {master.updatedOn && <> · {when(master.updatedOn)}</>}
                        </div>
                    </div>
                    <Button color={master.sendingOn ? "danger" : "success"} size="sm"
                        disabled={busy !== null} onClick={flipMaster}>
                        {busy === "master" ? "…" : master.sendingOn ? "Stop everything" : "Turn sending on"}
                    </Button>
                </div>
            </div>

            {/* What the sender is doing */}
            <div className="d-flex mb-3" style={{ gap: 12, flexWrap: "wrap" }}>
                <Tile label="Clients on" value={health?.clientsOn ?? 0} />
                <Tile label="Due now" value={health?.waiting ?? 0} strong={(health?.waiting ?? 0) > 0} />
                <Tile label="In a worker" value={health?.claimed ?? 0} />
                <Tile label="Sent today" value={health?.sentToday ?? 0} strong />
                <Tile label="Failed today" value={health?.failedToday ?? 0} />
                <Tile label="Last sent" value={when(health?.lastSentOn)} />
            </div>

            {/* The dry run */}
            {preview && (
                <div className="p-3 mb-3" style={panel}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="f-w-700 mb-0">
                            Dry run — {preview.claimed} claimed, nothing sent
                        </h6>
                        <Button color="link" size="sm" onClick={() => setPreview(null)}>Close</Button>
                    </div>

                    {(preview.messages ?? []).length === 0 ? (
                        <div className="text-muted" style={{ fontSize: 13 }}>
                            {preview.message || "Nothing is due."}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-sm align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: 70 }}>Ch</th>
                                        <th style={{ width: "18%" }}>To</th>
                                        <th style={{ width: 60 }} className="text-end">Step</th>
                                        <th>Message, exactly as it would go</th>
                                        <th style={{ width: "18%" }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.messages.map((m: any) => (
                                        <tr key={m.enrolmentId} style={m.canSend ? undefined : { background: "#fff8f6" }}>
                                            <td><Badge color={m.channel === "Email" ? "secondary" : "primary"}>{m.channel}</Badge></td>
                                            <td>
                                                <div className="f-w-600" style={{ fontSize: 13 }}>
                                                    {[m.firstName, m.surname].filter(Boolean).join(" ") || "—"}
                                                </div>
                                                <div className="text-muted" style={{ fontSize: 12 }}>{m.recipient || "no contact"}</div>
                                            </td>
                                            <td className="text-end">{m.nextStep}</td>
                                            <td>
                                                {m.subject && <div className="f-w-600" style={{ fontSize: 13 }}>{m.subject}</div>}
                                                <div style={{ fontSize: 13, color: "#444", whiteSpace: "pre-wrap" }}>{m.body}</div>
                                            </td>
                                            <td>
                                                {m.canSend
                                                    ? <Badge color="success">Would send</Badge>
                                                    : <span className="text-danger" style={{ fontSize: 12.5 }}>{m.cannotSendReason}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Per client */}
            <div className="p-3" style={panel}>
                <h6 className="f-w-700 mb-3">Client by client</h6>

                {loading ? (
                    <div className="text-center py-4"><Spinner size="sm" /></div>
                ) : companies.length === 0 ? (
                    <div className="text-muted py-3">No clients yet.</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th style={{ width: "24%" }}>Client</th>
                                    <th style={{ width: 110 }}>State</th>
                                    <th style={{ width: 170 }}>Send between</th>
                                    <th style={{ width: 110 }}>Sundays</th>
                                    <th style={{ width: 130 }}>Cap a day</th>
                                    <th className="text-end" style={{ width: 110 }}>Sent today</th>
                                    <th className="text-end" style={{ width: 130 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {companies.map((c: any) => (
                                    <tr key={c.companyId} style={c.sendingOn ? { background: "#f7fdf9" } : undefined}>
                                        <td className="f-w-600">
                                            {c.businessName}
                                            {c.sendingOn
                                                ? <Badge color="success" className="ms-2">On</Badge>
                                                : <Badge color="light" className="ms-2 text-dark">Off</Badge>}
                                        </td>
                                        <td>
                                            <select className="form-select form-select-sm"
                                                value={c.stateCode ?? ""}
                                                onChange={(e) => saveCompany(c, { stateCode: e.target.value || null })}>
                                                {STATES.map((s) => <option key={s || "none"} value={s}>{s || "—"}</option>)}
                                            </select>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1">
                                                <input type="number" min={0} max={23}
                                                    className="form-control form-control-sm" style={{ width: 62 }}
                                                    defaultValue={c.earliestHour}
                                                    onBlur={(e) => saveCompany(c, { earliestHour: e.target.value })} />
                                                <span className="text-muted" style={{ fontSize: 12 }}>to</span>
                                                <input type="number" min={0} max={23}
                                                    className="form-control form-control-sm" style={{ width: 62 }}
                                                    defaultValue={c.latestHour}
                                                    onBlur={(e) => saveCompany(c, { latestHour: e.target.value })} />
                                            </div>
                                            <div className="text-muted" style={{ fontSize: 11.5 }}>
                                                {hour(c.earliestHour)} – {hour(c.latestHour)}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="form-check form-switch mb-0">
                                                <input className="form-check-input" type="checkbox"
                                                    checked={!!c.sendOnSunday}
                                                    onChange={(e) => saveCompany(c, { sendOnSunday: e.target.checked })} />
                                            </div>
                                        </td>
                                        <td>
                                            <input type="number" min={0}
                                                className="form-control form-control-sm" style={{ width: 90 }}
                                                defaultValue={c.dailyCap}
                                                onBlur={(e) => saveCompany(c, { dailyCap: e.target.value })} />
                                        </td>
                                        <td className="text-end">
                                            {c.sentToday}
                                            {c.sentToday >= c.dailyCap && c.dailyCap > 0 && (
                                                <Badge color="warning" className="ms-1 text-dark">cap</Badge>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            <Button color={c.sendingOn ? "secondary" : "success"} size="sm" outline
                                                onClick={() => saveCompany(c, { sendingOn: !c.sendingOn })}>
                                                {c.sendingOn ? "Switch off" : "Switch on"}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <small className="text-muted d-block mt-2">
                    A client sends only when the master switch is on and their own is too, inside their window,
                    under their cap. Nothing sends on a Sunday unless it is switched on here.
                </small>
            </div>
        </div>
    );
};

const panel = {
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
} as const;

const Tile = ({ label, value, strong }: { label: string; value: any; strong?: boolean }) => (
    <div style={{
        border: "1px solid #e6e8f0", borderRadius: 8, padding: "10px 16px",
        background: strong ? "#f0f9f2" : "#fff", minWidth: 120,
    }}>
        <div style={{ fontSize: 11.5, color: "#767d95", fontWeight: 600, letterSpacing: 0.3 }}>{label}</div>
        <div style={{ fontSize: 19, fontWeight: 800, color: strong ? "#1a7f37" : "#111528" }}>{value}</div>
    </div>
);

export default CRMSendControl;
